import type { ReactNode, Ref } from "react";
import { base } from "./Card.css.ts";

export interface Props {
  /**
   * Background color preset.
   * @default "white"
   */
  backgroundColor?: keyof typeof base.classNames.variants.backgroundColor;
  /**
   * Corner radius preset.
   * @default 0
   */
  borderRadius?: keyof typeof base.classNames.variants.borderRadius;
  /**
   * Elevation shadow preset.
   * @default "l"
   */
  boxShadow?: keyof typeof base.classNames.variants.boxShadow;
  /**
   * Internal spacing preset.
   * @default 0
   */
  padding?: keyof typeof base.classNames.variants.padding;
  /**
   * Adds hover and focus-within styles, for cards that wrap interactive content (e.g. a link or button).
   * @default false
   */
  isInteractive?: boolean;
  /**
   * Outline option.
   * @default "transparent"
   */
  borderColor?: keyof typeof base.classNames.variants.borderColor;
  children: ReactNode;
  /** Ref to the underlying div element. */
  ref?: Ref<HTMLDivElement>;
}

/**
 * A container component for grouping related content with customizable background, border radius, shadow, and padding.
 */
export const Card: React.FC<Props> = ({
  backgroundColor = "white",
  borderRadius = 0,
  boxShadow = "l",
  borderColor = "transparent",
  padding = 0,
  isInteractive = false,
  children,
  ref,
}) => (
  <div
    className={base({
      backgroundColor,
      borderRadius,
      boxShadow,
      padding,
      isInteractive,
      borderColor,
    })}
    ref={ref}
  >
    {children}
  </div>
);
